import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import type { AssignmentMode } from "@/types/mission-assign"

/**
 * MissionAssignModeSwitchDialog — confirms a mode switch when the user has
 * a staged per-mode selection (segment_id or hand-pick ids) that would be
 * cleared by switching (UX Spec §1).
 *
 * The page renders this dialog with `pendingMode` set when the user picks
 * a different mode while a selection exists. Confirming calls `onConfirm`
 * (which kicks the actual `useAssignmentUrl#setMode` URL update);
 * cancelling closes the dialog without changing state.
 */
interface MissionAssignModeSwitchDialogProps {
  /** Mode the user wants to switch to. `null` keeps the dialog closed. */
  pendingMode: AssignmentMode | null
  onConfirm: () => void
  onCancel: () => void
  testId?: string
}

export function MissionAssignModeSwitchDialog({
  pendingMode,
  onConfirm,
  onCancel,
  testId = "mission-assign-mode-switch-dialog",
}: MissionAssignModeSwitchDialogProps) {
  const { t } = useTranslation()

  return (
    <Dialog
      open={pendingMode !== null}
      onOpenChange={(open) => {
        if (!open) onCancel()
      }}
    >
      <DialogContent data-testid={testId}>
        <DialogHeader>
          <DialogTitle>
            {t("missions.assign.mode_switch_dialog.title")}
          </DialogTitle>
          <DialogDescription>
            {t("missions.assign.mode_switch_dialog.body")}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            data-testid="mission-assign-mode-switch-cancel"
          >
            {t("missions.assign.mode_switch_dialog.cancel")}
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            data-testid="mission-assign-mode-switch-confirm"
            autoFocus
          >
            {t("missions.assign.mode_switch_dialog.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
